import React, { useEffect, useMemo, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, FormControl } from '@mui/material';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import CustomColumnApiService from '../api/CustomColumnApiService';
import EditMenuButton from './Modal/EditMenuButton';
import EditCustomColumnModal from './Modal/Admin/EditCustomColumnModal';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from './Common/SnackbarProvider';

const customColumnApiService = new CustomColumnApiService();

const CustomColumns = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const [customColumns, setCustomColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100);
    const [currentSelectedCustomColumn, setCurrentSelectedCustomColumn] = useState();
    const [openEditCustomColumnModal, setOpenEditCustomColumnModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
            </GridToolbarContainer>
        );
    }

    const columns = useMemo(() => [
        {
            field: 'customColumnId',
            headerName: t('common.id'),
            flex: 0.25,
        },
        {
            field: 'customColumnName',
            headerName: t('fieldName.name'),
            flex: 0.5,
        },
        {
            field: 'dataTypeFix',
            headerName: t('customColumnsPage.column.datatype'),
            flex: 0.5,
        },
        {
            field: 'dataTypeLength',
            headerName: t('customColumnsPage.column.length'),
            flex: 0.5,
        },
        {
            field: 'inputType',
            headerName: t('customColumnsPage.column.inputType'),
            flex: 0.5,
        },
        {
            field: 'description',
            headerName: t('fieldName.description'),
            flex: 1.5,
        },
        {
            field: 'options',
            headerName: t('customColumnsPage.column.options'),
            flex: 1.5,
        },
        {
            field: 'editableByAssignee',
            headerName: t('customColumnsPage.column.editableByAssignee'),
            flex: 0.75,
            type: 'boolean',
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setCurrentSelectedCustomColumn}
                    setShowEditDialog={setOpenEditCustomColumnModal}
                    onEditFunc={() => {
                        setOpenEditCustomColumnModal(true);
                        setCurrentSelectedCustomColumn(params.row);
                    }}
                />;
            },
        },
    ], [t]);

    const transformedRows = useMemo(() => customColumns.map((row) => {
        const datatype = row.datatype ?? '';
        const commaIndex = datatype.indexOf(',');
        const dataTypeFix = commaIndex > -1 ? datatype.substring(0, commaIndex) : datatype;
        const dataTypeLength = commaIndex > -1 ? datatype.substring(commaIndex + 1).trim() : '';
        return { ...row, dataTypeFix, dataTypeLength };
    }), [customColumns]);

    useEffect(() => {
        async function setupCustomColumns () {
            setLoading(true);
            await populateCustomColumns();
            setLoading(false);
        }

        setupCustomColumns();
    }, []);

    async function populateCustomColumns () {
        let customColumnsResponse = await customColumnApiService.getCustomColumns();
        setCustomColumns(customColumnsResponse.data);
    }

    const dialogTitle = () => {
        return t('customColumnsPage.confirmDeleteTitle', { name: currentSelectedCustomColumn?.customColumnName ?? '' });
    };

    const dialogLabel = () => {
        return t('customColumnsPage.confirmDeleteLabel', { name: currentSelectedCustomColumn?.customColumnName ?? '' });
    };

    async function onDeleteLocationClick (deleteValue) {
        if (deleteValue) {
            let deleteCustomColumnResponse = await customColumnApiService.deleteCustomColumn(currentSelectedCustomColumn);
            if (deleteCustomColumnResponse.success === true)
            {
                snackbar.success(t('customColumnsPage.toast.deleted', { name: currentSelectedCustomColumn.customColumnName }));
                await populateCustomColumns();
            }
        }

        setShowDeleteDialog(false);
        setCurrentSelectedCustomColumn(null);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Typography variant="h4" sx={{ marginBottom: '16px' }}>
                {t('customColumnsPage.title')}
            </Typography>
            <FormControl sx={{ marginBottom: '8px' }}>
                <Button size={'large'} variant="contained" onClick={() => setOpenEditCustomColumnModal(true)} >{t('customColumnsPage.createButton')}</Button>
            </FormControl>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    rows={transformedRows}
                    columns={columns}
                    loading={loading}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    getRowId={(row) => row.customColumnId}
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                />
            </Box>
            <EditCustomColumnModal
                open={openEditCustomColumnModal}
                currentCustomColumn={currentSelectedCustomColumn}
                onCancel={() => {
                    setOpenEditCustomColumnModal(false);
                    setCurrentSelectedCustomColumn(null);
                }}
                onSave={async () => {
                    await populateCustomColumns();
                    setOpenEditCustomColumnModal(false);
                    setCurrentSelectedCustomColumn(null);
                }}
            />
            <ConfirmDeleteDialogHazard
                keepMounted
                value={currentSelectedCustomColumn}
                open={showDeleteDialog}
                onClose={onDeleteLocationClick}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </Box>
    );
};
export default CustomColumns;
